import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { useDeployAuth } from "../../../context/DeployAuthContext";
import LimeLogo from "../../../assets/lime-logo-no-bg.png";

const api = axios.create({ baseURL: "", withCredentials: true });

const STOREFRONT_BASE_DOMAIN = process.env.REACT_APP_STOREFRONT_BASE_DOMAIN || "limelite.store";
const POLL_INTERVAL_MS = 15000;
const BUILDING_MESSAGES = [
  "Spinning up your server…",
  "Configuring SSL certificates…",
  "Installing storefront…",
  "Setting up your database…",
  "Running health checks…",
  "Almost there…",
];

function Step({ label, state }) {
  // state: "done" | "active" | "waiting"
  return (
    <div className="flex items-center gap-4">
      <div className="w-8 h-8 flex-shrink-0 flex items-center justify-center">
        {state === "done" && (
          <div className="w-8 h-8 rounded-full bg-lime-600/20 border border-lime-600 flex items-center justify-center">
            <svg className="w-4 h-4 text-lime-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
            </svg>
          </div>
        )}
        {state === "active" && (
          <div className="w-8 h-8 rounded-full bg-lime-900/20 border border-lime-800/50 flex items-center justify-center">
            <div className="w-4 h-4 border-2 border-lime-500 border-t-transparent rounded-full animate-spin" />
          </div>
        )}
        {state === "waiting" && (
          <div className="w-8 h-8 rounded-full border border-gray-800 flex items-center justify-center">
            <div className="w-2 h-2 rounded-full bg-gray-700" />
          </div>
        )}
      </div>
      <span className={`text-sm font-medium transition-colors ${
        state === "done" ? "text-lime-300" :
        state === "active" ? "text-silver-light" :
        "text-silver-dark"
      }`}>
        {label}
      </span>
    </div>
  );
}

export default function StorefrontProvisioningPage() {
  const navigate = useNavigate();
  const { isAuthenticated } = useDeployAuth();

  const [project, setProject] = useState(null);
  const [loadError, setLoadError] = useState("");
  const [msgIndex, setMsgIndex] = useState(0);
  const [elapsed, setElapsed] = useState(0);

  const pollRef = useRef(null);
  const msgRef = useRef(null);
  const elapsedRef = useRef(null);
  // Anchor for elapsed timer — prefer project.createdAt so refresh doesn't reset it
  const startAnchorRef = useRef(Date.now());

  const status = project?.status || "pending";
  const isActive = status === "active";
  const isFailed = status === "failed";

  const poll = async () => {
    try {
      const { data } = await api.get("/api/storefront/billing/subscription");
      // If there's no active subscription, payment didn't complete — bounce back
      if (!data.subscription || !["active", "trialing"].includes(data.subscription.status)) {
        clearInterval(pollRef.current);
        navigate("/limelite/storefront?checkout=cancel", { replace: true });
        return;
      }
      setProject(data.project || null);
      // Anchor elapsed timer to when provisioning actually began (payment confirmed),
      // not project.createdAt — a reserved row can predate checkout by a long time and
      // would otherwise show a misleading "59m elapsed" on a fresh purchase.
      const anchor = data.project?.provisioningStartedAt || data.project?.createdAt;
      if (anchor) {
        startAnchorRef.current = new Date(anchor).getTime();
      }
      if (data.project?.status === "active" || data.project?.status === "failed") {
        clearInterval(pollRef.current);
      }
    } catch {
      setLoadError("Connection lost — retrying…");
      setTimeout(() => setLoadError(""), 4000);
    }
  };

  useEffect(() => {
    if (!isAuthenticated) {
      navigate("/limelite/storefront", { replace: true });
      return;
    }

    poll();
    pollRef.current = setInterval(poll, POLL_INTERVAL_MS);

    msgRef.current = setInterval(() => {
      setMsgIndex(i => (i + 1) % BUILDING_MESSAGES.length);
    }, 4000);

    elapsedRef.current = setInterval(() => {
      setElapsed(Math.floor((Date.now() - startAnchorRef.current) / 1000));
    }, 1000);

    return () => {
      clearInterval(pollRef.current);
      clearInterval(msgRef.current);
      clearInterval(elapsedRef.current);
    };
  }, [isAuthenticated]); // eslint-disable-line react-hooks/exhaustive-deps

  // Map backend statuses to step states.
  // Backend lifecycle (StorefrontProject.status): pending → provisioning_db →
  // provisioning_infra → active (or failed). The old code checked status === "provisioning",
  // which the backend never emits, so steps 1–2 never reflected real progress.
  function stepState(step) {
    if (isActive) return "done";
    if (step === 0) return "done"; // Payment confirmed always done on this page
    if (step === 1) {
      // Setting up infrastructure / database (Atlas M0)
      if (status === "provisioning_infra") return "done";
      return "active"; // pending or provisioning_db
    }
    if (step === 2) {
      // Installing storefront (EC2 deploy + seed)
      if (status === "provisioning_infra") return "active";
      return "waiting";
    }
    if (step === 3) return "waiting"; // Going live — flips to done via isActive above
    return "waiting";
  }

  const subdomain = project?.subdomain;
  const storeUrl = project?.storeUrl || (subdomain ? `https://${subdomain}.${STOREFRONT_BASE_DOMAIN}` : null);
  const displayUrl = subdomain ? `${subdomain}.${STOREFRONT_BASE_DOMAIN}` : null;

  return (
    <div className="bg-black text-silver-light min-h-[calc(100vh-4rem)] flex flex-col items-center justify-center px-4 py-16">
      <div className="w-full max-w-md">

        {/* Logo */}
        <div className="flex items-center justify-center gap-2 mb-12">
          <img src={LimeLogo} alt="Limelite" className="w-7 h-7" />
          <span className="text-xs uppercase tracking-widest text-lime-500">Limelite Storefront</span>
        </div>

        {isFailed ? (
          /* ── Error state ── */
          <div className="text-center">
            <div className="w-14 h-14 rounded-full bg-red-900/20 border border-red-800 flex items-center justify-center mx-auto mb-6">
              <svg className="w-7 h-7 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
            <h1 className="text-2xl font-serif font-medium mb-3">Setup failed</h1>
            <p className="text-silver-dark text-sm mb-2 leading-relaxed">
              {project?.statusMessage || "Something went wrong during setup."}
            </p>
            <p className="text-silver-dark text-sm mb-8">
              Your subscription is active — reach out and we'll get this sorted.
            </p>
            <Link
              to="/limelite/storefront/dashboard"
              className="inline-flex items-center text-sm text-lime-400 hover:text-lime-300 border border-lime-800/50 hover:border-lime-700 rounded-lg px-4 py-2 transition-colors"
            >
              Go to dashboard →
            </Link>
          </div>
        ) : isActive ? (
          /* ── Success state ── */
          <div className="text-center">
            <div className="w-14 h-14 rounded-full bg-lime-600/20 border border-lime-600 flex items-center justify-center mx-auto mb-6">
              <svg className="w-7 h-7 text-lime-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h1 className="text-2xl font-serif font-medium mb-3">Your store is live.</h1>
            {displayUrl && (
              <p className="text-silver-dark text-sm mb-8">
                Up and running at{" "}
                <span className="text-lime-400 font-mono">{displayUrl}</span>
              </p>
            )}
            <div className="flex flex-col gap-3">
              {storeUrl && (
                <a
                  href={storeUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center bg-lime-600 hover:bg-lime-500 text-black font-bold py-3 px-8 rounded-xl transition-colors"
                >
                  Open your store →
                </a>
              )}
              {project?.adminUrl && (
                <a
                  href={project.adminUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center border border-gray-700 hover:border-gray-500 text-silver-light text-sm font-medium py-2.5 px-6 rounded-xl transition-colors"
                >
                  Open merchant dashboard →
                </a>
              )}
              <Link
                to="/limelite/storefront/dashboard"
                className="text-sm text-silver-dark hover:text-silver-light transition-colors text-center"
              >
                Go to account dashboard →
              </Link>
            </div>
          </div>
        ) : (
          /* ── Progress state ── */
          <>
            <div className="text-center mb-10">
              <h1 className="text-2xl font-serif font-medium mb-2">Setting up your store</h1>
              {displayUrl ? (
                <p className="text-silver-dark text-sm">
                  Your store address:{" "}
                  <span className="text-silver-medium font-mono">{displayUrl}</span>
                </p>
              ) : (
                <p className="text-silver-dark text-sm">This usually takes 3–5 minutes</p>
              )}
            </div>

            {/* Steps */}
            <div className="space-y-5 mb-10">
              <Step label="Payment confirmed" state={stepState(0)} />
              <Step
                label={stepState(1) === "active" ? BUILDING_MESSAGES[msgIndex] : "Setting up infrastructure"}
                state={stepState(1)}
              />
              <Step label="Installing storefront" state={stepState(2)} />
              <Step label="Going live" state={stepState(3)} />
            </div>

            {/* Subtle elapsed time */}
            <p className="text-center text-xs text-gray-700">
              {elapsed < 60
                ? `${elapsed}s elapsed`
                : `${Math.floor(elapsed / 60)}m ${elapsed % 60}s elapsed`}
            </p>

            {loadError && (
              <p className="text-center text-xs text-amber-600 mt-3">{loadError}</p>
            )}
          </>
        )}

        {/* Footer link */}
        {!isActive && !isFailed && (
          <p className="text-center text-xs text-gray-700 mt-12">
            <Link to="/limelite/storefront/dashboard" className="hover:text-silver-dark transition-colors">
              View account dashboard
            </Link>
          </p>
        )}
      </div>
    </div>
  );
}
